import type { AxiosError } from "axios";
import type { BaseResponse } from "./api-contract";

interface JwtPayload {
  exp?: number;
}

const PUBLIC_AUTH_ENDPOINTS = [
  /(?:^|\/)v1\/auth\/login(?:[/?#]|$)/,
  /(?:^|\/)v1\/auth\/forgot-password(?:[/?#]|$)/,
  /(?:^|\/)v1\/auth\/validate-reset-token(?:[/?#]|$)/,
  /(?:^|\/)v1\/auth\/reset-password(?:[/?#]|$)/,
];

/** Collapses duplicate path separators without changing the URL protocol. */
export function normalizeUrl(url: string): string {
  return url.replace(/([^:]\/)\/+/g, "$1");
}

export function isPublicAuthRequest(url?: string): boolean {
  return !!url && PUBLIC_AUTH_ENDPOINTS.some((pattern) => pattern.test(url));
}

function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;

    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    return JSON.parse(atob(padded)) as JwtPayload;
  } catch {
    return null;
  }
}

export function isTokenExpiringSoon(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (typeof payload?.exp !== "number") return true;

  const refreshSkewMs = 30 * 1000;
  return Date.now() >= payload.exp * 1000 - refreshSkewMs;
}

export function getNormalizedErrorMessage(error: AxiosError): string | undefined {
  const data = error.response?.data as Partial<BaseResponse<unknown>> | undefined;
  return data?.user_message || data?.message || error.message;
}

export function isDevelopment(): boolean {
  return process.env.NODE_ENV === "development";
}
